import { useTranslation } from 'react-i18next';

import styled from '@emotion/styled';
import {
  BankAccountResponseDto,
  CreatorGradeCode,
  IdentityVerificationResponseDto,
  MyProfileResponseDto,
  resolveCreatorGradeCode,
} from '@repo/services/hooks';
import { CreatorGradeBadge } from '@repo/ui';
import { getServiceName } from '@repo/utils';

import { translation } from 'utils/translation';

import Flex from 'components/util/Flex';

import { BankAccountModificationButton, BankAccountVerificationButton } from './BankAccountButton';
import { EmailModificationButton, EmailVerificationButton } from './EmailButton';
import { IdentityVerificationButton } from './IdentityButton';
import { SAdditionalInformation, SInformationItem } from '../shared/style';

/**
 * 등급 이름 (기획 확정 2026-09-16 — 리워드 페이지는 짧은 형태로 쓴다).
 * 어드민은 "별가루 크리에이터" 처럼 긴 형태를 쓰지만 여기는 유저 노출 지면이라 기획이 짧게 정했다.
 * 서버는 등급 코드만 내려주고 표시 이름은 주지 않아(swagger 전수 확인) 클라이언트가 가진다.
 */
const CREATOR_GRADE_NAME_KEY = {
  STARDUST: 'chat.creator_grade_stardust',
  RISING_STAR: 'chat.creator_grade_rising_star',
  STARKEEPER: 'chat.creator_grade_starkeeper',
} as const satisfies Record<Exclude<CreatorGradeCode, 'NONE'>, string>;

/** 번역 검수 완료 전까지 쓰는 기본값. locale json 에 값이 들어오면 그쪽이 우선한다 */
const CREATOR_GRADE_NAME_FALLBACK = {
  STARDUST: '별가루',
  RISING_STAR: '샛별',
  STARKEEPER: '별지기',
} as const satisfies Record<Exclude<CreatorGradeCode, 'NONE'>, string>;

type CreatorInformationProps = {
  profileData?: MyProfileResponseDto;
  bankAccountData?: BankAccountResponseDto;
  identityData?: IdentityVerificationResponseDto;
};

function CreatorInformation({ profileData, identityData, bankAccountData }: CreatorInformationProps) {
  const { t } = useTranslation();
  // 본인 인증 완료 여부
  const isIdentityVerificationCompleted = Boolean(identityData);
  // 이메일 인증 완료 여부
  const isEmailVerificationCompleted = profileData?.verified ?? false;

  // 크리에이터 등급 (#2039). 칸 안에 [등급 이름 + 뱃지] 를 함께 보여준다(기획 확정 2026-09-16).
  // 서버는 코드 문자열만 주고 표시 이름은 내려주지 않아(swagger 전수 확인) 이름은 클라이언트가 가진다.
  // 0단계(NONE)면 행 자체를 숨긴다 — 일반 유저에게 등급 행을 보여줄 이유가 없다.
  // 팅글은 등급제 미적용 — 서버가 등급 코드를 내려줘도 칸 자체를 그리지 않는다.
  const resolvedGrade = resolveCreatorGradeCode(profileData);
  const hasCreatorGrade = resolvedGrade !== 'NONE' && getServiceName() !== 'tingle';
  // NONE 이거나 팅글이면 행을 안 그리므로, 아래 렌더 블록이 읽는 시점의 등급은 항상 1~3단계다
  const creatorGrade = resolvedGrade as Exclude<CreatorGradeCode, 'NONE'>;

  // 계좌 인증 가능 여부
  const isBankAccountVerificationEnabled =
    (isEmailVerificationCompleted && isIdentityVerificationCompleted) || bankAccountData?.status === 'verified';
  // 계좌 인증 완료 여부
  const isBankAccountVerificationCompleted = bankAccountData?.status === 'verified';

  // 은행 계좌가 등록되었는지 여부 판단 (Admin에서 본인 인증을 풀 수 있는 엣지 케이스 대응)
  const isBankAccountAssigned = bankAccountData?.status === 'verified';

  const rowGap = isBankAccountVerificationCompleted ? 10 : 12;

  return (
    <>
      <Flex direction="column" rowGap={rowGap}>
        {/* 유저이름 */}
        <SInformationItemWrapper $paddingBottom={16} $paddingTop={16}>
          <span>{t('chat.rewards_creator_information_username')}</span>
          <span className="bold">{profileData?.username}</span>
        </SInformationItemWrapper>
        {/* 크리에이터 등급 — 칸 안에 [등급 이름 + 뱃지] (기획 확정 2026-09-16) */}
        {hasCreatorGrade && (
          <SInformationItemWrapper>
            {/* 번역 시트 `검수대기` 탭에 등록했고 검수 대기 중이다(2026-09-17 요청).
                검수 후 `BubbleChat`·`Tingle` 탭으로 옮겨야 `pnpm translate` 가 읽는다.
                그 전까지 키 문자열이 그대로 노출되지 않도록 기본값을 준다. (#2039) */}
            <span className="no-wrap">{t('chat.rewards_creator_information_grade', '크리에이터 등급')}</span>
            <Flex align="center" columnGap={6}>
              <span className="bold">
                {t(CREATOR_GRADE_NAME_KEY[creatorGrade], CREATOR_GRADE_NAME_FALLBACK[creatorGrade])}
              </span>
              <CreatorGradeBadge user={profileData} size={20} />
            </Flex>
          </SInformationItemWrapper>
        )}
        {/* 본인 인증 여부 */}
        <SInformationItemWrapper>
          <span>{t('chat.rewards_creator_information_identity_verification')}</span>
          <Flex align="center" columnGap={16}>
            <span className="bold">{identityData?.name}</span>
            {isIdentityVerificationCompleted ? (
              <IdentityVerificationButton
                identityData={identityData!}
                isVerificationCompleted={isIdentityVerificationCompleted}
              />
            ) : (
              <IdentityVerificationButton isVerificationCompleted={isIdentityVerificationCompleted} />
            )}
          </Flex>
        </SInformationItemWrapper>
        {/* 이메일 인증 여부 */}
        <SInformationItemWrapper>
          <span className="no-wrap">{t('chat.rewards_creator_information_email')}</span>
          <Flex align="center" columnGap={16} justify="flex-end" className="email-wrapper">
            <p className="bold email ellipsis">{profileData?.email}</p>
            {isEmailVerificationCompleted ? (
              <EmailModificationButton />
            ) : (
              <EmailVerificationButton isVerificationCompleted={isEmailVerificationCompleted} />
            )}
          </Flex>
        </SInformationItemWrapper>
        {/* 계좌 */}
        <SBankInformationItemWrapper $isBankAccountVerificationCompleted={isBankAccountVerificationCompleted}>
          <Flex direction="column" isExpanded rowGap={10}>
            {/* 계좌 인증 여부 */}
            <Flex justify="space-between" align="center">
              <span>{t('chat.rewards_creator_information_account_verification')}</span>
              {isBankAccountVerificationCompleted ? (
                <BankAccountModificationButton />
              ) : (
                <BankAccountVerificationButton
                  isVerificationEnabled={isBankAccountVerificationEnabled}
                  isVerificationCompleted={isBankAccountVerificationCompleted}
                />
              )}
            </Flex>
            {/* 은행 정보 */}
            {isBankAccountAssigned && (
              <BankAccountInformation
                bankName={bankAccountData.bankName}
                accountHolder={bankAccountData.accountHolder}
                accountNumber={bankAccountData.accountNumber}
              />
            )}
          </Flex>
        </SBankInformationItemWrapper>
      </Flex>
      {/* 본인 인증 및 이메일 인증 체크 */}
      {!isBankAccountVerificationEnabled && (
        <SAdditionalInformation>
          <span>{t('chat.rewards_verify_identify_email_account')}</span>
        </SAdditionalInformation>
      )}
    </>
  );
}

export default CreatorInformation;

type SInformationItemWrapperProps = {
  $paddingTop?: number;
  $paddingBottom?: number;
};
const SInformationItemWrapper = styled(SInformationItem)<SInformationItemWrapperProps>`
  padding-top: ${({ $paddingTop }) => ($paddingTop ? `${$paddingTop}px` : '0px')};
  padding-bottom: ${({ $paddingBottom }) => ($paddingBottom ? `${$paddingBottom}px` : '12px')};

  .email-wrapper {
    width: calc(100% - 40px);
  }

  .email {
    max-width: 55%;
  }
`;

type SBankInformationItemWrapperProps = {
  $isBankAccountVerificationCompleted: boolean;
};

const SBankInformationItemWrapper = styled(SInformationItem)<SBankInformationItemWrapperProps>`
  padding: ${({ $isBankAccountVerificationCompleted }) =>
    $isBankAccountVerificationCompleted ? '0px 0px 10px 0' : '0 0 12px 0px'};
`;

const SBankAccountInformation = styled.div`
  padding: 10px 0px;

  ${({ theme }) => theme.font.body3_m};

  .bold {
    ${({ theme }) => theme.font.body3_m};
  }
`;

type BankAccountInformationProps = {
  accountHolder: string; // 예금주명
  bankName: string; // 은행명
  accountNumber: string; // 계좌번호
};
const BankAccountInformation = ({ accountHolder, accountNumber, bankName }: BankAccountInformationProps) => {
  return (
    <Flex direction="column" isExpanded>
      <SBankAccountInformation>
        <Flex align="center" justify="space-between">
          <span>{translation('chat.rewards_account_holder')}</span>
          <span className="bold">{accountHolder}</span>
        </Flex>
      </SBankAccountInformation>
      <SBankAccountInformation>
        <Flex align="center" justify="space-between">
          <span>{translation('chat.rewards_bank')}</span>
          <span className="bold">{bankName}</span>
        </Flex>
      </SBankAccountInformation>
      <SBankAccountInformation>
        <Flex align="center" justify="space-between">
          <span>{translation('chat.rewards_account_number')}</span>
          <span className="bold">{accountNumber}</span>
        </Flex>
      </SBankAccountInformation>
    </Flex>
  );
};
